"use client";
import React, { useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { DateRangePicker } from '@/components/molecules/DatePicker';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardActions } from '../hooks/useDashboardActions';

interface DateRangeSelectorProps {
    className?: string;
    label?: string;
}

export function DashboardDateRangeSelector({ className, label = "Dates" }: DateRangeSelectorProps) {
    const { state } = useDashboard();
    const { setSelectedDateRange, setSelectedCompareEnabled, setSelectedCompareDateRange } = useDashboardActions();
    const { selectedDateRange, selectedCompareEnabled, selectedCompareDateRange, hydrated } = state;

    const getMonthToDate = () => {
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
            from: firstDayOfMonth,
            to: today
        };
    };

    const defaultValue = useMemo(() => {
        return selectedDateRange || getMonthToDate();
    }, [selectedDateRange]);

    useEffect(() => {
        if (hydrated && !selectedDateRange) {
            setSelectedDateRange(getMonthToDate());
        }
    }, [hydrated, selectedDateRange, setSelectedDateRange]);

    if (!hydrated) {
        return (
            <div className={classNames('flex', 'items-center', 'gap-4')}>
                <p className="text-[14px] font-roboto font-medium" style={{ color: '#232F34' }}>{label}</p>
                <div className="w-48 h-10 bg-gray-100 animate-pulse rounded"></div>
            </div>
        );
    }

    return (
        <div className={classNames('flex', 'items-center', 'gap-4')}>
            <p className="text-[14px] font-roboto font-medium" style={{ color: '#232F34' }}>{label}</p>
            <DateRangePicker
                defaultValue={defaultValue}
                showQuickSelect
                showCompare
                compare={selectedCompareEnabled}
                compareRange={selectedCompareDateRange ?? undefined}
                className={classNames('font-roboto', 'font-medium', className)}
                onChange={(value) => {
                    console.log('Date range changed:', value);
                    if (value?.from && value?.to) {
                        setSelectedDateRange({ from: value.from, to: value.to });
                    }
                }}
                onCompareChange={(checked) => {
                    setSelectedCompareEnabled(checked);
                    if (!checked) {
                        setSelectedCompareDateRange(null);
                    }
                }}
                onCompareRangeChange={(value) => {
                    if (value?.from && value?.to) {
                        setSelectedCompareDateRange({ from: value.from, to: value.to });
                    } else {
                        setSelectedCompareDateRange(null);
                    }
                }}
            />
        </div>
    );
}
